import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE = 'https://rvapi.emedha.in';

const SelfPacedArena = () => {
  const { quizCode } = useParams();

  // Loading & Quiz State
  const [loading, setLoading] = useState(true);
  const [quizInfo, setQuizInfo] = useState(null);
  const [questions, setQuestions] = useState([]);

  // Registration & Resume State
  const [joined, setJoined] = useState(false);
  const [participantId, setParticipantId] = useState(null);
  const [playerInfo, setPlayerInfo] = useState({ name: '', email: '', mobile: '' });
  const [isVerifying, setIsVerifying] = useState(false);

  // Active Question Loop State
  const [currentIndex, setCurrentIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [isCorrectSelection, setIsCorrectSelection] = useState(false);
  const [scoreFeedback, setScoreFeedback] = useState(0);

  // Scorecard State
  const [totalScore, setTotalScore] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [finished, setFinished] = useState(false);

  // Styling Constants Matching Live Arena
  const colors = {
    orange: '#f35018',
    blue: '#2f3194',
    cyan: '#63decc',
    textMain: '#0f1137'
  };
  const premiumColors = ['#e21b3c', '#1368ce', '#d89e00', '#26890c'];
  const shapes = ['▲', '◆', '●', '■'];

  // 1. Initial Load: Fetch Quiz and check LocalStorage session
  useEffect(() => {
    axios.get(`${API_BASE}/pages/api/api/quiz/self-paced/${quizCode}`)
      .then(res => {
        setQuizInfo(res.data.quizInfo);
        const qList = res.data.questions || [];
        setQuestions(qList);

        // Check if user already joined on this device
        const savedSession = localStorage.getItem(`self_paced_session_${quizCode}`);
        if (savedSession) {
          try {
            const parsed = JSON.parse(savedSession);
            autoResume(parsed.mobile, parsed.name, parsed.email, qList, res.data.quizInfo);
          } catch (e) {
            console.error('Session parse failed', e);
          }
        }
      })
      .catch(err => {
        alert(err.response?.data?.error || 'Invalid or expired quiz link.');
      })
      .finally(() => setLoading(false));
  }, [quizCode]);

  // 2. Auto-resume helper function
  const autoResume = async (mobile, name, email, qList, qInfo) => {
    try {
      const res = await axios.post(`${API_BASE}/pages/api/api/quiz/self-paced/join-or-resume`, {
        quizCode,
        mobile,
        name: name || '',
        email: email || ''
      });

      applyParticipantData(res.data, qList, qInfo);
    } catch (err) {
      console.error('Auto resume failed:', err);
    }
  };

  const applyParticipantData = (data, qList, qInfo) => {
    setParticipantId(data.participantId);
    setPlayerInfo({ name: data.name, email: data.email, mobile: data.mobile });
    setTotalScore(data.totalScore || 0);
    setCorrectCount(data.correctCount || 0);
    setWrongCount(data.wrongCount || 0);
    setJoined(true);

    if (data.isCompleted || (data.currentIndex >= (qList?.length || questions.length))) {
      setFinished(true);
    } else {
      setCurrentIndex(data.currentIndex || 0);
      setTimeLeft(qInfo?.seconds_per_question || 30);
    }
  };

  // 3. Question Countdown Loop
  useEffect(() => {
    if (!joined || finished || hasAnswered || questions.length === 0) return;

    if (timeLeft <= 0) {
      handleTimeExpiration();
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [joined, finished, timeLeft, hasAnswered, questions.length]);

  // 4. Handle Join / Registration Submission
  const handleJoinSubmit = async (e) => {
    e.preventDefault();
    if (!playerInfo.mobile.trim()) {
      alert('Mobile number is required.');
      return;
    }

    try {
      setIsVerifying(true);
      const res = await axios.post(`${API_BASE}/pages/api/api/quiz/self-paced/join-or-resume`, {
        quizCode,
        name: playerInfo.name,
        email: playerInfo.email,
        mobile: playerInfo.mobile
      });

      // Persist in local storage for refresh recovery
      localStorage.setItem(`self_paced_session_${quizCode}`, JSON.stringify({
        mobile: playerInfo.mobile,
        name: playerInfo.name,
        email: playerInfo.email
      }));

      applyParticipantData(res.data, questions, quizInfo);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to authenticate session.');
    } finally {
      setIsVerifying(false);
    }
  };

  // 5. Submit Question Selection
  const handleOptionClick = async (selectedOptionText, selectedImage = null) => {
    if (hasAnswered) return;

    const currentQ = questions[currentIndex];
    if (!currentQ) return;

    // Evaluate answer correctness (Text or Image quiz)
    const correctAnswer = currentQ.correct_answer;
    const correctImage = currentQ.correct_image;

    let checkCorrect = false;
    if (correctImage) {
      checkCorrect = (selectedImage === correctImage) || (selectedOptionText === correctImage);
    } else if (correctAnswer) {
      checkCorrect = (selectedOptionText === correctAnswer);
    }

    // Scoring math matching Live Arena
    const basePoints = 10;
    const speedBonus = checkCorrect ? Math.round(timeLeft * 1.5) : 0;
    const pointsAwarded = checkCorrect ? (basePoints + speedBonus) : 0;
    const totalAllowed = quizInfo?.seconds_per_question || 30;
    const secondsTaken = Math.max(0, totalAllowed - timeLeft);

    setHasAnswered(true);
    setIsCorrectSelection(checkCorrect);
    setScoreFeedback(pointsAwarded);
    setTotalScore(prev => prev + pointsAwarded);

    if (checkCorrect) {
      setCorrectCount(prev => prev + 1);
    } else {
      setWrongCount(prev => prev + 1);
    }

    const nextIdx = currentIndex + 1;
    const isCompleted = nextIdx >= questions.length;

    // Send result to database
    try {
      await axios.post(`${API_BASE}/pages/api/api/quiz/self-paced/submit-answer`, {
        participantId,
        quizCode,
        questionId: currentQ.id,
        selectedOption: selectedOptionText || selectedImage,
        isCorrect: checkCorrect,
        secondsTaken,
        secondsLeft: timeLeft,
        pointsAwarded,
        nextIndex: nextIdx,
        isCompleted
      });
    } catch (err) {
      console.error('Answer submission error:', err);
    }

    // Show brief feedback before advancing
    setTimeout(() => {
      if (isCompleted) {
        setFinished(true);
      } else {
        setCurrentIndex(nextIdx);
        setTimeLeft(quizInfo?.seconds_per_question || 30);
        setHasAnswered(false);
        setIsCorrectSelection(false);
        setScoreFeedback(0);
      }
    }, 1800);
  };

  // 6. Handle Zero-Seconds Timeout
  const handleTimeExpiration = async () => {
    setHasAnswered(true);
    setIsCorrectSelection(false);
    setScoreFeedback(0);
    setWrongCount(prev => prev + 1);

    const currentQ = questions[currentIndex];
    const nextIdx = currentIndex + 1;
    const isCompleted = nextIdx >= questions.length;

    try {
      await axios.post(`${API_BASE}/pages/api/api/quiz/self-paced/submit-answer`, {
        participantId,
        quizCode,
        questionId: currentQ.id,
        selectedOption: 'TIMEOUT',
        isCorrect: false,
        secondsTaken: quizInfo?.seconds_per_question || 30,
        secondsLeft: 0,
        pointsAwarded: 0,
        nextIndex: nextIdx,
        isCompleted
      });
    } catch (err) {
      console.error('Timeout logging error:', err);
    }

    setTimeout(() => {
      if (isCompleted) {
        setFinished(true);
      } else {
        setCurrentIndex(nextIdx);
        setTimeLeft(quizInfo?.seconds_per_question || 30);
        setHasAnswered(false);
        setIsCorrectSelection(false);
      }
    }, 1800);
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh', background: '#0b0c26', color: '#63decc' }}>
        <div className="text-center">
          <div className="spinner-border mb-3" role="status"></div>
          <h4>Loading Quiz Arena...</h4>
        </div>
      </div>
    );
  }

  if (!quizInfo) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh', background: '#0b0c26', color: '#ff6b6b' }}>
        <h4>Quiz Session Not Found or Has Expired.</h4>
      </div>
    );
  }

  const currentQ = questions[currentIndex];

  return (
    <div style={{ minHeight: '100vh', background: 'radial-gradient(circle at top right, #1e1f4b 0%, #0b0c26 100%)', fontFamily: '"Plus Jakarta Sans", "Poppins", sans-serif', color: '#ffffff', padding: '30px 15px' }}>
      <div className="container" style={{ maxWidth: '780px' }}>
        
        {/* VIEW 1: REGISTRATION & RESUME PROMPT */}
        {!joined ? (
          <div className="p-4 p-md-5 rounded-4 shadow-lg mx-auto" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', backdropFilter: 'blur(16px)', maxWidth: '480px' }}>
            <div className="text-center mb-4">
              <div style={{ fontSize: '48px', marginBottom: '10px' }}>🎮</div>
              <h3 className="fw-bold m-0" style={{ letterSpacing: '-0.5px' }}>{quizInfo.title}</h3>
              <span className="badge mt-2 px-3 py-1.5 rounded-pill" style={{ backgroundColor: 'rgba(99, 222, 204, 0.15)', color: colors.cyan, border: '1px solid rgba(99, 222, 204, 0.3)' }}>
                {questions.length} Questions &bull; {quizInfo.seconds_per_question}s per question
              </span>
            </div>

            <form onSubmit={handleJoinSubmit}>
              <div className="mb-3">
                <label className="small fw-bold  mb-1">Full Name *</label>
                <input 
                  required 
                  type="text" 
                  className="form-control px-3 py-2 bg-white text-dark" 
                  placeholder="Enter your name" 
                  value={playerInfo.name} 
                  onChange={e => setPlayerInfo({ ...playerInfo, name: e.target.value })} 
                />
              </div>

              <div className="mb-3">
                <label className="small fw-bold  mb-1">Email Address *</label>
                <input 
                  required 
                  type="email" 
                  className="form-control px-3 py-2 bg-white text-dark" 
                  placeholder="Enter your email" 
                  value={playerInfo.email} 
                  onChange={e => setPlayerInfo({ ...playerInfo, email: e.target.value })} 
                />
              </div>

              <div className="mb-4">
                <label className="small fw-bold  mb-1">Mobile Number (Used to Resume) *</label>
                <input 
                  required 
                  type="tel" 
                  className="form-control px-3 py-2 bg-white text-dark" 
                  placeholder="10-digit mobile number" 
                  value={playerInfo.mobile} 
                  onChange={e => setPlayerInfo({ ...playerInfo, mobile: e.target.value })} 
                />
                <small className="" style={{ fontSize: '11px' }}>
                  If you joined before, entering your mobile number restores your session right where you left off.
                </small>
              </div>

              <button 
                type="submit" 
                disabled={isVerifying} 
                className="btn w-100 fw-bold py-2.5 text-white" 
                style={{ background: `linear-gradient(135deg, ${colors.orange}, #ff7342)`, border: 'none', borderRadius: '12px' }}
              >
                {isVerifying ? 'Verifying Session...' : 'Enter Quiz Arena 🚀'}
              </button>
            </form>
          </div>
        ) : !finished ? (

          /* VIEW 2: ACTIVE QUESTION SCREEN */
          <div>
            {/* Top Navigation & Status Bar */}
            <div className="d-flex justify-content-between align-items-center mb-4 px-2">
              <span className="fw-bold" style={{ color: colors.cyan, fontSize: '15px' }}>
                Question {currentIndex + 1} of {questions.length}
              </span>

              {/* Timer Badge */}
              <div style={{
                background: timeLeft <= 5 ? '#e21b3c' : 'rgba(255,255,255,0.06)',
                border: `2px solid ${timeLeft <= 5 ? '#e21b3c' : colors.cyan}`,
                borderRadius: '50%', width: '65px', height: '65px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '22px', fontWeight: '800', color: '#ffffff',
                boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
                transition: 'all 0.3s ease'
              }}>
                {timeLeft}s
              </div>
            </div>

            {/* Question Text Box */}
            <div className="bg-white text-dark p-4 rounded-4 text-center mb-4 shadow" style={{ borderBottom: `4px solid ${colors.orange}` }}>
              <h4 className="fw-bold m-0" style={{ fontSize: '20px', lineHeight: '1.4', color: colors.textMain }}>
                {currentQ.question}
              </h4>
            </div>

            {/* Answer Options Grid (4-box layout supporting images) */}
            <div className="row g-3">
              {[0, 1, 2, 3].map((idx) => {
                const optText = currentQ[`option_${idx + 1}`];
                const optImg = currentQ[`images${idx + 1}`];

                if (!optText && !optImg && idx >= 2) return null;
                const optionLabel = optText || `Option ${idx + 1}`;

                return (
                  <div key={idx} className="col-12 col-md-6">
                    <button
                      type="button"
                      disabled={hasAnswered}
                      onClick={() => handleOptionClick(optionLabel, optImg)}
                      className="w-100 border-0 text-white fw-bold shadow-lg d-flex flex-column align-items-center justify-content-center p-3"
                      style={{
                        background: premiumColors[idx],
                        minHeight: optImg ? '150px' : '110px',
                        borderRadius: '16px',
                        position: 'relative',
                        opacity: hasAnswered ? 0.75 : 1,
                        transform: hasAnswered ? 'none' : 'scale(1)',
                        transition: 'all 0.2s ease',
                        cursor: hasAnswered ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span style={{ fontSize: '18px', opacity: 0.85 }}>{shapes[idx]}</span>
                        {optText && <span style={{ fontSize: '15px' }}>{optText}</span>}
                      </div>

                      {/* Render Option Image If Provided */}
                      {optImg && (
                        <div className="my-1 w-100 text-center">
                          <img 
                            src={optImg} 
                            alt={`Option ${idx + 1}`} 
                            style={{ maxHeight: '90px', maxWidth: '85%', borderRadius: '8px', objectFit: 'contain', background: 'rgba(255,255,255,0.2)', padding: '4px' }} 
                          />
                        </div>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Instant Answer Feedback Banner */}
            {hasAnswered && (
              <div className="text-center mt-4 p-3 rounded-3 animate-fade-in" style={{ background: isCorrectSelection ? 'rgba(38, 137, 12, 0.2)' : 'rgba(226, 27, 60, 0.2)', border: `1px solid ${isCorrectSelection ? '#26890c' : '#e21b3c'}` }}>
                <h5 className="fw-bold m-0" style={{ color: isCorrectSelection ? '#22c55e' : '#ef4444' }}>
                  {isCorrectSelection ? `✅ Correct! +${scoreFeedback} pts` : '❌ Incorrect / Time Expired'}
                </h5>
              </div>
            )}
          </div>
        ) : (

          /* VIEW 3: PODIUM & FINAL SCORECARD SCREEN */
          <div className="p-4 p-md-5 rounded-4 shadow-lg text-center animate-fade-in" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', backdropFilter: 'blur(16px)' }}>
            <div style={{ fontSize: '64px', marginBottom: '10px' }}>🏆</div>
            <h2 className="fw-bold text-white mb-1">Quiz Completed!</h2>
            <p className=" small mb-4">
              Great effort, <strong className="text-white">{playerInfo.name}</strong>! Here is your finalized performance summary:
            </p>

            <div className="p-4 mb-4 rounded-4 shadow-lg mx-auto" style={{
              maxWidth: '360px',
              background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.01) 100%)',
              border: `1px solid ${colors.cyan}`
            }}>
              <span className="text-uppercase small tracking-wide fw-bold d-block mb-1" style={{ color: colors.cyan }}>
                Total Accumulated Score
              </span>
              <h1 className="fw-extrabold m-0 text-warning" style={{ fontSize: '52px' }}>
                {totalScore} <span className="small fw-normal " style={{ fontSize: '16px' }}>pts</span>
              </h1>
            </div>

            <div className="row g-3 justify-content-center mb-4">
              <div className="col-6 col-sm-4">
                <div className="p-3 rounded-3" style={{ background: 'rgba(38, 137, 12, 0.15)', border: '1px solid rgba(38, 137, 12, 0.3)' }}>
                  <span className="d-block small fw-bold text-success text-uppercase mb-1">Correct</span>
                  <h3 className="fw-bold m-0 text-white">✅ {correctCount}</h3>
                </div>
              </div>
              <div className="col-6 col-sm-4">
                <div className="p-3 rounded-3" style={{ background: 'rgba(226, 27, 60, 0.15)', border: '1px solid rgba(226, 27, 60, 0.3)' }}>
                  <span className="d-block small fw-bold text-danger text-uppercase mb-1">Incorrect</span>
                  <h3 className="fw-bold m-0 text-white">❌ {wrongCount}</h3>
                </div>
              </div>
            </div>

            <p className=" small mb-0">
              Your scorecard metrics and timestamps have been permanently logged.
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

export default SelfPacedArena;